import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Bell, Ticket, PartyPopper } from "lucide-react";
import { LOGO_URL, INTEREST_HREF, PERFORM } from "../../lib/constants";
import { useUpcomingShows } from "../../lib/shows";
import { SHOWS_HREF } from "../Shows";
import { NAV, ROUTES } from "../../lib/seo";
import { trackNotifyClick, trackPerformClick } from "../../lib/track";

// The header CTA follows what the site is actually asking for. While a Bhaav
// Sangam show is ahead it is "Book tickets", which scrolls to the Upcoming
// Shows section on the home page (there are two cities, so it cannot be one
// BookMyShow link). Once both have passed it is "Host Antara": the site's
// first ask is now Antara at the visitor's own occasion. The interest list
// stays a tap away in the mobile panel.
const BOOK_CTA = { href: SHOWS_HREF, onClick: undefined, icon: Ticket, label: "Book tickets", testid: "nav-ticket-btn" };
const HOST_CTA = { to: ROUTES.host.path, onClick: undefined, icon: PartyPopper, label: "Host Antara", testid: "nav-host-btn" };

// One pill, as a router Link when it points at a page and a plain anchor
// when it points at a section.
function CtaPill({ cta, className, style, onClick }) {
  const inner = <><cta.icon size={14} /> {cta.label}</>;
  if (cta.to) {
    return <Link to={cta.to} onClick={onClick} className={className} style={style} data-testid={cta.testid}>{inner}</Link>;
  }
  return <a href={cta.href} onClick={onClick} className={className} style={style} data-testid={cta.testid}>{inner}</a>;
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const CTA = useUpcomingShows().length ? BOOK_CTA : HOST_CTA;

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-500 ${
        scrolled ? "bg-[#0F0A07]/90 backdrop-blur-md border-b border-[#C9A961]/20" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" aria-label="Antara — home">
          <img src={LOGO_URL} alt="Antara" width="36" height="36" className="h-8 w-8 md:h-9 md:w-9 object-contain"
            style={{ filter: "drop-shadow(0 2px 8px rgba(184,118,62,0.5))" }} />
          <span className="font-display text-lg md:text-xl" style={{ color: "#F5EFE6" }}>Antara</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7">
          {NAV.slice(1).map((r) => (
            <NavLink
              key={r.path}
              to={r.path}
              className={({ isActive }) =>
                `font-body text-[13px] tracking-wide transition-colors ${
                  isActive ? "text-[#E5C880]" : "text-[#E8D9C5]/85 hover:text-[#E5C880]"
                }`
              }
            >
              {r.crumb}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <CtaPill
            cta={CTA}
            onClick={CTA.onClick}
            className="hidden sm:inline-flex items-center gap-2 rounded-full px-4 py-2 font-body text-xs tracking-wide"
            style={{ background: "linear-gradient(135deg,#E5C880,#C9A961)", color: "#2A1F17", fontWeight: 600 }}
          />
          <button
            className="lg:hidden p-2 -mr-2 text-[#F5EFE6]"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      {open && (
        <nav className="lg:hidden bg-[#0F0A07]/97 backdrop-blur-md border-t border-[#C9A961]/20 px-5 py-4" data-testid="mobile-nav-panel">
          <ul className="flex flex-col">
            {NAV.map((r) => (
              <li key={r.path}>
                <NavLink
                  to={r.path}
                  className={({ isActive }) =>
                    `block py-3 font-body text-base border-b border-[#C9A961]/10 ${
                      isActive ? "text-[#E5C880]" : "text-[#E8D9C5]"
                    }`
                  }
                >
                  {r.crumb}
                </NavLink>
              </li>
            ))}
            {[ROUTES.host, ROUTES.sponsorship].map((r) => (
              <li key={r.path}>
                <NavLink
                  to={r.path}
                  className={({ isActive }) =>
                    `block py-3 font-body text-base border-b border-[#C9A961]/10 ${isActive ? "text-[#E5C880]" : "text-[#E5C880]/90"}`
                  }
                >
                  {r.crumb}
                </NavLink>
              </li>
            ))}
            <li>
              <a
                href={INTEREST_HREF}
                onClick={() => { trackNotifyClick(); setOpen(false); }}
                className="flex items-center gap-2 py-3 font-body text-base border-b border-[#C9A961]/10 text-[#E5C880]/90"
                data-testid="nav-interest-btn"
              >
                <Bell size={15} /> Keep me posted
              </a>
            </li>
            {PERFORM.open && (
              <li key={ROUTES.perform.path}>
                <NavLink
                  to={ROUTES.perform.path}
                  onClick={trackPerformClick}
                  className="block py-3 font-body text-base border-b border-[#C9A961]/10 text-[#E5C880]"
                >
                  Perform at Antara
                </NavLink>
              </li>
            )}
          </ul>
          <CtaPill
            cta={{ ...CTA, testid: `${CTA.testid}-mobile` }}
            onClick={() => { if (CTA.onClick) CTA.onClick(); setOpen(false); }}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full px-4 py-3 font-body text-sm"
            style={{ background: "linear-gradient(135deg,#E5C880,#C9A961)", color: "#2A1F17", fontWeight: 600 }}
          />
        </nav>
      )}
    </header>
  );
}
